import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import { SWRConfig } from "swr";
import type { HttpConfiguration } from "../hooks/http";
import type { HttpClient } from "../lib/http";

/**
 * a map of http clients used within the http context
 */
export type HttpClientMap = { [name: string]: HttpClient };

/**
 * define a custom http context object
 */
type HttpContextObj<T extends HttpClientMap = {}> = {
  clients: T;
};

const HttpContext = createContext<HttpContextObj>({} as HttpContextObj);
export function useHttpContext<T extends HttpClientMap>(): HttpContextObj<T> {
  return useContext(HttpContext) as HttpContextObj<T>;
}

/**
 * properties for the http provider component
 */
type HttpProviderProps<T extends HttpClientMap> = PropsWithChildren<{
  clients: T;
  httpConfig?:
    | HttpConfiguration
    | ((parentConfig?: HttpConfiguration) => HttpConfiguration);
}>;

/**
 * combine SWR configuraiton with custom http context (for fetchers)
 * @see https://swr.vercel.app/docs/global-configuration
 * @returns
 */
export function HttpProvider<T extends HttpClientMap>({
  clients,
  httpConfig,
  children,
}: HttpProviderProps<T>) {
  // merge clients through heirarchy
  const { clients: parentClients = {} } = useHttpContext();

  const contents = (
    <HttpContext.Provider
      value={{
        clients: { ...parentClients, ...clients },
      }}
    >
      {children}
    </HttpContext.Provider>
  );

  if (httpConfig) {
    return <SWRConfig value={httpConfig}>{contents}</SWRConfig>;
  }

  return contents;
}
